import { IEquipment } from '@models/Equipment.model';
import { EquipmentStatusCategoryOptions } from '@models/EquipmentStatus.model';
import { ITagLimitWithDesc } from '@models/Tag.model';
import { NeededFromEq } from '@pages/Products/components/OnBoardingModal/OnBoardingModal.utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mutateKnownData } from './knownData.helper';

export interface KnownProductType {
  id: string;
  model: string | null;
  imageUrl: string | null;
  manufacturerName: string | null;
  // Todo: known manufacturers
  manufacturerImg: string | null | undefined;
}

export interface IKnownStatus {
  id: string;
  name: string;
  category: EquipmentStatusCategoryOptions;
}

export interface KnownUserType {
  id: string;
  name: string | null;
}
const initialState: KnownData = {
  knownProducts: {},
  knownUsers: {},
  knownTags: {},
  knownStatuses: {},
  knownEquipments: {},
};
export interface KnownFrom<T> {
  [id: string]: T;
}
export type IKnownTag = KnownFrom<ITagLimitWithDesc>;
export type KnownProducts = KnownFrom<KnownProductType>;
export type IKnownStatuses = KnownFrom<IKnownStatus>;
export type IKnownEquipments = KnownFrom<EqRet>;
export interface KnownData {
  knownProducts: KnownProducts;
  knownUsers: KnownFrom<KnownUserType>;
  knownTags: IKnownTag;
  knownStatuses: IKnownStatuses;
  knownEquipments: IKnownEquipments;
}

export const knownDataSlice = createSlice({
  name: 'Known Data',
  initialState,
  reducers: {
    reset: () => initialState,
    addKnownProduct: (state, knownProduct: PayloadAction<KnownProductType>) => {
      state.knownProducts[knownProduct.payload.id] = knownProduct.payload;
    },
    addKnownUser: (state, knownUsers: PayloadAction<KnownUserType>) => {
      state.knownUsers[knownUsers.payload.id] = knownUsers.payload;
    },
    addKnownTag: (
      state,
      knownTag: PayloadAction<ITagLimitWithDesc | ITagLimitWithDesc[]>,
    ) => {
      if (knownTag.payload instanceof Array) {
        knownTag.payload.forEach(tag => {
          state.knownTags[tag.id] = tag;
        });
      } else {
        state.knownTags[knownTag.payload.id] = knownTag.payload;
      }
    },
    setKnownTags: (state, knownTag: PayloadAction<IKnownTag>) => {
      state.knownTags = knownTag.payload;
    },
    addKnownStatus: (
      state,
      knownStatus: PayloadAction<IKnownStatus | IKnownStatus[]>,
    ) => {
      if (knownStatus.payload instanceof Array) {
        knownStatus.payload.forEach(status => {
          state.knownStatuses[status.id] = status;
        });
      } else {
        state.knownStatuses[knownStatus.payload.id] = knownStatus.payload;
      }
    },
    addKnownEquipments(state, knownEquipments: PayloadAction<EqRet | EqRet[]>) {
      if (knownEquipments.payload instanceof Array) {
        knownEquipments.payload.forEach(equipment => {
          state.knownEquipments[equipment.id] = equipment;
        });
      } else {
        state.knownEquipments[knownEquipments.payload.id] =
          knownEquipments.payload;
      }
    },
    addKnownFromEquipments(
      state,
      equipments: PayloadAction<
        NeededFromEq | NeededFromEq[] | Map<string, NeededFromEq> | KnownData
      >,
    ) {
      if ('knownProducts' in equipments.payload) {
        state.knownEquipments = equipments.payload.knownEquipments;
        state.knownProducts = equipments.payload.knownProducts;
        state.knownStatuses = equipments.payload.knownStatuses;
        state.knownTags = equipments.payload.knownTags;
        state.knownUsers = equipments.payload.knownUsers;
      } else if (equipments.payload instanceof Map) {
        equipments.payload.forEach(equipment => {
          mutateKnownData(state, equipment);
        });
      } else if (equipments.payload instanceof Array) {
        equipments.payload.forEach(equipment => {
          mutateKnownData(state, equipment);
        });
      } else {
        mutateKnownData(state, equipments.payload);
      }
    },
  },
});
export const {
  reset: resetKnownData,
  addKnownProduct,
  addKnownUser,
  setKnownTags,
  addKnownTag,
  addKnownStatus,
  addKnownEquipments,
  addKnownFromEquipments,
} = knownDataSlice.actions;

export const { reducer: knownDataReducer } = knownDataSlice;
export type EqRet = Omit<IEquipment, 'tags'> & {
  tags: [];
};
